import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';

const statusColors = { applied: 'badge-blue', screening: 'badge-yellow', interview: 'badge-purple', offer: 'badge-green', hired: 'badge-green', rejected: 'badge-red' };

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/jobs/${id}`)
      .then(res => setData(res.data))
      .catch(() => navigate('/jobs'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this job?')) return;
    await api.delete(`/jobs/${id}`);
    navigate('/jobs');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
    </div>
  );

  const { job, applications } = data || {};

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/jobs')} className="text-sm text-slate-500 hover:text-slate-700 mb-2">← Jobs</button>
          <h1 className="text-2xl font-bold text-slate-800">{job?.title}</h1>
          <div className="flex gap-2 mt-2">
            <span className="badge badge-blue">
              🌍 {job?.market}
            </span>
            <span className="badge badge-slate">{job?.location}</span>
            <span className="badge badge-slate">{job?.employment_type}</span>
            {job?.status === 'active' ? <span className="badge badge-green">Active</span> : <span className="badge badge-red">{job?.status}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/jobs/${id}/edit`} className="btn-secondary text-sm">Edit</Link>
          <button onClick={handleDelete} className="btn-danger text-sm">Delete</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Description</h3>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{job?.description || 'No description provided.'}</p>
            {job?.requirements && (
              <>
                <h3 className="font-semibold text-slate-800 mt-5 mb-3">Requirements</h3>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{job.requirements}</p>
              </>
            )}
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Screened Candidates</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to={`/cv-match`}
                state={{ jobId: job?.id, jobTitle: job?.title, jobDescription: job?.description }}
                className="btn-primary text-sm"
              >
                ⚡ Screen CVs for this Job
              </Link>
              <Link
                to={`/history?tab=screenings&job=${encodeURIComponent(job?.title || '')}`}
                className="btn-secondary text-sm"
              >
                📋 View Screening Results
              </Link>
              <Link
                to={`/pipeline?job=${encodeURIComponent(job?.title || '')}`}
                className="btn-secondary text-sm"
              >
                🔄 View Pipeline
              </Link>
            </div>
          </div>
        </div>

        <div className="card p-5 h-fit">
          <h3 className="font-semibold text-slate-800 mb-4">Job Details</h3>
          <div className="space-y-3 text-sm">
            {job?.salary_min && (
              <div className="flex justify-between">
                <span className="text-slate-500">Salary</span>
                <span className="font-medium text-slate-700">
                  {job.salary_currency} {job.salary_min.toLocaleString()}{job.salary_max ? `–${job.salary_max.toLocaleString()}` : '+'}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">Department</span>
              <span className="font-medium text-slate-700">{job?.department}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Posted</span>
              <span className="font-medium text-slate-700">{job?.created_at ? new Date(job.created_at).toLocaleDateString() : '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Posted by</span>
              <span className="font-medium text-slate-700">{job?.created_by_name}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
